/**
 * «Marcar resueltas las que tienen la ventana cerrada» (§5.6). Después de una campaña grande, «Sin
 * responder» se llenaba de miles de «Gracias» que ya sólo se contestan pagando plantilla. Es una tarjeta
 * dentro de la página, no un modal (R2): se lee qué hace antes de apretar.
 *
 * No le manda nada a nadie. Al terminar avisa el cambio: la lista y el badge se vuelven a pedir solos.
 */

import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ResultadoResolverVencidas } from "../../types.conversaciones";
import { llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";
import { avisarCambioConversaciones } from "../../logic/conversaciones";
import { num, plural } from "../../../campana-nueva/utils/format";

export function TarjetaResolverVencidas({ vencidas, onCerrar }: { vencidas: number; onCerrar: () => void }) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState<number | null>(null);
  const enCurso = useRef(false);

  const resolver = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setEnviando(true);
    setError("");
    const l = await llamar<ResultadoResolverVencidas>(() => conversacionesApi.resolverVencidas());
    enCurso.current = false;
    setEnviando(false);
    if (!l.ok) { setError(textoError(l)); return; }
    setListo(Number(l.data?.resueltas) || 0);
    avisarCambioConversaciones();
  };

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 text-sm text-slate-700" aria-labelledby="titulo-resolver-vencidas">
      <h3 id="titulo-resolver-vencidas" className="font-semibold text-slate-900">Marcar resueltas las que tienen la ventana cerrada</h3>
      {listo === null ? (
        <>
          <p className="mt-2">
            Se {plural(vencidas, "marca como resuelta", "marcan como resueltas")} {num(vencidas)} {plural(vencidas, "conversación en la que pasaron", "conversaciones en las que pasaron")} más de 24 h desde el último mensaje. No se
            le manda nada a nadie. Si alguien vuelve a escribir, su conversación vuelve a «Por contestar».
          </p>
          {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={() => void resolver()} disabled={enviando}
              className="h-9 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {enviando && <Loader2 size={14} className="animate-spin" />} Marcar resueltas
            </button>
            <button type="button" onClick={onCerrar} disabled={enviando} className="h-9 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold">Cancelar</button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-emerald-800" role="status">Listo: se {plural(listo, "marcó", "marcaron")} {num(listo)}.</p>
          <button type="button" onClick={onCerrar} className="mt-3 h-9 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold">Cerrar</button>
        </>
      )}
    </section>
  );
}

export default TarjetaResolverVencidas;
